import React, {useState, useEffect} from "react";
import {Button, Popover, Divider, Typography, FormControlLabel, Switch, ThemeProvider} from "@mui/material";
import TuneIcon from '@mui/icons-material/Tune';

import {GridColumnFilterStateSaving} from "./columnfilterComponent";
import {switchTheme} from "../utils/muiStyling";

import {encryptAndStoreSessionVariable} from "../utils/storageHelper";

import {MuiIconButtonWithTooltip, MuiIconWithTooltip} from "./muiComponents";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

export const DisplayOptions = ({useFilterStateSettingToggled,
                                   setUseFilterStateSettingToggled,
                                   toggleUpdateUseFilterState,
                                   useColumnStateSettingToggled,
                                   setUseColumnStateSettingToggled,
                                   toggleUpdateUseColumnState,
                                   showAllTelemetryDataToggled,
                                   toggleUpdateShowAllTelemetryData,

                                   fullGridToggled,
                                   setFullGridToggled,
                                   updateFullIncidentGridEnabledReactive,
                                   fullGridEnabledSessionVariableName,

                                   showClientAuthIconToggled,
                                   setShowClientAuthIconToggled,
                                   showGroupInstallerConfigIconToggledSessionVariable,
                                   updateShowGroupInstallerConfigIconToggledReactive,
                                   gridApi,

                                   chartToggled,
                                   setChartToggled,
                                   chartSessionVariableName,
                                   updateChartVisibilitySettingInSession,
                                   updateChartVisibilityReactive,
                                   includeWhitelistedToggled,
                                   setIncludeWhitelistedToggled,
                                   includeWhitelistedToggleDisabled,
                                   chartName,

                                   dealsPage,
                                   canViewDealVolumeDiscounts,

                                   filterOutExpiredLicensesToggled,
                                   setFilterOutExpiredLicensesToggled,
                                   filterExpiredLicensesByDefaultVariable,
                                   updateFilterExpiredLicensesByDefaultReactive,
                                   getCurrentDateAgGridFilterString,

                                   // Grid view toggle — used for pages that can switch
                                   // between two grid modes (e.g. hourly vs daily).
                                   // gridViewToggled: boolean — true = secondary view active
                                   // setGridViewToggled: (boolean) => void
                                   // gridViewToggleLabel: string — describes the secondary
                                   //   view, e.g. "Hourly View" (shown when toggled ON)
                                   // gridViewToggleOffLabel: string — optional override for
                                   //   the OFF label, defaults to "Daily View"
                                   gridViewToggled,
                                   setGridViewToggled,
                                   gridViewToggleLabel,
                                   gridViewToggleOffLabel,
                               }) => {

    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'display-options-popover' : undefined;

    useEffect(() => {
        const handleResize = () => {
            if (open) {
                handleClose();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [open]);

    const offLabel = gridViewToggleOffLabel || "Daily View";

    return (
        <div className={"flex flex-wrap width-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white/60 px-5 py-4 shadow-sm"}>
            <Button
                aria-describedby={id}
                variant="contained"
                onClick={handleClick}
                startIcon={<TuneIcon/>}
                sx={{
                    "&:hover": {
                        backgroundColor: "#e6e6e6",
                        cursor: "pointer",
                        boxShadow: 'none',
                    },
                    background: "white",
                    color: "#e76a24",
                    border: "1px solid #e76a24",
                    width: "200px",
                    p: 1.3,
                    pl: 1.8,
                    pr: 1.8,
                    borderRadius: '9999px',
                    boxShadow: 'none',
                }}
            >
                Display Options
            </Button>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        minWidth: 350,
                        px: 2.5,
                        py: 1.5,
                    }
                }}
            >
                <div className={"flex flex-col items-left gap-y-2"}>

                    {/* ── Grid view toggle (e.g. hourly vs daily) ── */}
                    {setGridViewToggled && (
                        <>
                            <Typography variant="overline" color="text.secondary" letterSpacing={1.5} sx={{fontSize: 11}}>
                                Grid View
                            </Typography>
                            <ThemeProvider theme={switchTheme}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={gridViewToggled}
                                            name="gridViewToggle"
                                            onChange={e => setGridViewToggled(e.target.checked)}
                                        />
                                    }
                                    label={gridViewToggled
                                        ? (gridViewToggleLabel || "Secondary View")
                                        : offLabel
                                    }
                                />
                            </ThemeProvider>
                            <Divider sx={{my: 1}}/>
                        </>
                    )}

                    {/* ── Chart visibility toggle ── */}
                    {updateChartVisibilityReactive && (
                        <>
                            {(!dealsPage || canViewDealVolumeDiscounts) && (
                                <>
                                    <Typography variant="overline" color="text.secondary" letterSpacing={1.5} sx={{fontSize: 11}}>
                                        Charts
                                    </Typography>
                                    <div className={"flex flex-row items-center"}>
                                        <ThemeProvider theme={switchTheme}>
                                            <FormControlLabel control={
                                                <Switch
                                                    checked={chartToggled}
                                                    name="licenseUsageToggle"
                                                    onChange={e => {
                                                        setChartToggled(e.target.checked)
                                                        updateChartVisibilitySettingInSession(chartSessionVariableName, e.target.checked, updateChartVisibilityReactive)
                                                    }}
                                                />
                                            } label={chartToggled ? `Showing ${chartName} Chart` : `Hiding ${chartName} Chart`}/>
                                        </ThemeProvider>
                                        {dealsPage && (
                                            <MuiIconWithTooltip
                                                icon={
                                                    <FontAwesomeIcon
                                                        className="mt-1 mr-1 object-contain cursor-pointer"
                                                        icon="fa-light fa-circle-info"
                                                        size="lg"
                                                    />
                                                }
                                                tooltipTitle={"These tables show additional discounts that are applied to deals that match the criteria. When you click on a deal, the tables will highlight any matching discount that would " +
                                                    "get applied to the deal"}
                                                tooltipPlacement={"bottom-start"}
                                            />
                                        )}
                                    </div>
                                    {setIncludeWhitelistedToggled && (
                                        <ThemeProvider theme={switchTheme}>
                                            <FormControlLabel control={
                                                <Switch
                                                    disabled={includeWhitelistedToggleDisabled}
                                                    checked={includeWhitelistedToggled}
                                                    name="includeWhitelistedIncidentsToggle"
                                                    onChange={e => setIncludeWhitelistedToggled(e.target.checked)}
                                                />
                                            } label={includeWhitelistedToggled ? "Showing Excluded Responses" : "Hiding Excluded Responses"}/>
                                        </ThemeProvider>
                                    )}
                                    <Divider sx={{my: 1}}/>
                                </>
                            )}
                        </>
                    )}

                    {/* ── Grid defaults ── */}
                    <Typography variant="overline" color="text.secondary" letterSpacing={1.5} sx={{fontSize: 11}}>Grid defaults</Typography>
                    <GridColumnFilterStateSaving
                        useFilterStateSettingToggled={useFilterStateSettingToggled}
                        setUseFilterStateSettingToggled={setUseFilterStateSettingToggled}
                        toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                        useColumnStateSettingToggled={useColumnStateSettingToggled}
                        setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                        toggleUpdateUseColumnState={toggleUpdateUseColumnState}
                        showAllTelemetryDataToggled={showAllTelemetryDataToggled}
                        toggleUpdateShowAllTelemetryData={toggleUpdateShowAllTelemetryData}
                    />

                    {updateFullIncidentGridEnabledReactive && (
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={fullGridToggled}
                                    name="fullGridToggled"
                                    onChange={toggleSetting => {
                                        setFullGridToggled(toggleSetting.target.checked)
                                        updateChartVisibilitySettingInSession(fullGridEnabledSessionVariableName, toggleSetting.target.checked, updateFullIncidentGridEnabledReactive)
                                    }}
                                />
                            } label={fullGridToggled ? "Show Grid with all Columns" : "Show Grid with Inner Details Grid"}/>
                        </ThemeProvider>
                    )}

                    {setShowClientAuthIconToggled && (
                        <ThemeProvider theme={switchTheme}>
                            <FormControlLabel control={
                                <Switch
                                    checked={showClientAuthIconToggled}
                                    name="showGroupClientAuthIconToggle"
                                    color={"primary"}
                                    onChange={toggleSetting => {
                                        setShowClientAuthIconToggled(toggleSetting.target.checked)
                                        updateChartVisibilitySettingInSession(showGroupInstallerConfigIconToggledSessionVariable, toggleSetting.target.checked, updateShowGroupInstallerConfigIconToggledReactive)
                                        gridApi && gridApi.refreshCells({force: true, columns: ["friendlyName"], suppressFlash: true})
                                    }}
                                />
                            } label={
                                <div className={"flex flex-row gap-x-1 gap-y-2 items-center flex-wrap"}>
                                    {showClientAuthIconToggled ? "Show Installer Script Config Icon" : "Do Not Show Installer Script Config Icon"}
                                    <MuiIconButtonWithTooltip
                                        icon={
                                            <FontAwesomeIcon
                                                className="object-contain"
                                                icon="fa-solid fa-circle-info"
                                                size="xs"
                                                color={"#949494"}
                                            />
                                        }
                                        tooltipTitle={`${showClientAuthIconToggled ? "Clicking this icon in the Group column below will show the group id and client auth for the group used in the agent installer script" :
                                            "When this icon is shown in the Group column below, clicking it will show the group id and client auth for the group used in the agent installer script"}`}
                                        tooltipPlacement={"bottom-start"}
                                    />
                                </div>
                            }/>
                        </ThemeProvider>
                    )}

                    {setFilterOutExpiredLicensesToggled && (
                        <ThemeProvider theme={switchTheme}>
                            <div className={`flex flex-row items-center flex-nowrap gap-x-0`}>
                                <FormControlLabel control={
                                    <Switch
                                        checked={filterOutExpiredLicensesToggled}
                                        name="filterOutExpiredLicensesToggled"
                                        color={"primary"}
                                        onChange={toggleSetting => {
                                            if (gridApi !== null && gridApi !== undefined) {
                                                setFilterOutExpiredLicensesToggled(toggleSetting.target.checked)
                                                if (toggleSetting.target.checked) {
                                                    let startTimeFilterString = getCurrentDateAgGridFilterString()
                                                    let nonExpiredFilter = {
                                                        "dateFrom": startTimeFilterString,
                                                        "dateTo": null,
                                                        "filterType": "date",
                                                        "type": "greaterThan"
                                                    }
                                                    let filterModel = gridApi.getFilterModel();
                                                    if (filterModel === null || filterModel === undefined) {
                                                        filterModel = {}
                                                    }
                                                    filterModel["expirationDate"] = nonExpiredFilter
                                                    gridApi.setFilterModel(filterModel)
                                                } else {
                                                    let filterModel = gridApi.getFilterModel();
                                                    if (filterModel === null || filterModel === undefined) {
                                                        filterModel = {}
                                                    }
                                                    filterModel["expirationDate"] = null
                                                    gridApi.setFilterModel(filterModel)
                                                }
                                                encryptAndStoreSessionVariable(filterExpiredLicensesByDefaultVariable, JSON.stringify(toggleSetting.target.checked))
                                                updateFilterExpiredLicensesByDefaultReactive(toggleSetting.target.checked).then(response => {}).catch(error => {
                                                    console.log(error);
                                                })
                                            }
                                        }}
                                    />
                                } label={filterOutExpiredLicensesToggled ? "Filter Expired Licenses from Grid and Chart by Default" : "Do Not Filter Expired Licenses from Grid and Chart by Default"}
                                />
                                <MuiIconWithTooltip
                                    icon={<FontAwesomeIcon className="mt-1 mr-1 object-contain cursor-pointer" icon="fa-light fa-circle-info" size="lg"/>}
                                    tooltipTitle={"This setting is for automatically filtering out expired licenses from the grid and chart. Note that when this setting is turned on, it will override" +
                                        " any saved filter for the Renews column"}
                                    tooltipPlacement={"bottom-start"}
                                />
                            </div>
                        </ThemeProvider>
                    )}
                </div>
            </Popover>
            {setChartToggled && (
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                    {chartToggled ? 'Chart Visible' : 'Chart Hidden'}
                </div>
            )}
        </div>
    );
};